import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import { MOTION_STATUS_CODES } from "../multiDay/eventIcons";

// Reading a trip currently means opening every event in turn. These are the
// figures that answer "what happened on this trip" at a glance, derived
// from the points already on the client - no new API calls.

const IDLE_CODES = new Set<VehicleStatusCode>([
  VehicleStatusCode.STATUS_MOTION_IDLE,
  VehicleStatusCode.STATUS_EXCESS_IDLE,
]);

const STOPPED_CODES = new Set<VehicleStatusCode>([
  VehicleStatusCode.STATUS_MOTION_STOP,
  VehicleStatusCode.STATUS_MOTION_STOPPED,
  VehicleStatusCode.STATUS_MOTION_DORMANT,
]);

// PARKED - not shown anywhere, and deliberately so. Which codes count as a
// violation is per-account: the figure is meant to become a list of the
// event types that account cares about and a count against each, chosen by
// the customer (seat belt matters to some, idle or a camera event to
// others). A single number off a fixed list of codes would be a guess at
// that, so the chip came out of the trip summary until the configuration
// exists. The codes below are kept as the starting point for it.
const VIOLATION_CODES = new Set<VehicleStatusCode>([
  VehicleStatusCode.STATUS_HARSH_BEHAVIOR,
  VehicleStatusCode.STATUS_EXCESS_BRAKING,
  VehicleStatusCode.STATUS_EXCESS_BRAKING_2,
  VehicleStatusCode.STATUS_EXCESS_BRAKING_3,
  VehicleStatusCode.STATUS_EXCESS_BRAKING_4,
  VehicleStatusCode.STATUS_EXCESS_CORNERING,
  VehicleStatusCode.STATUS_EXCESS_CORNERING_2,
  VehicleStatusCode.STATUS_EXCESS_CORNERING_3,
  VehicleStatusCode.STATUS_EXCESS_CORNERING_4,
  VehicleStatusCode.STATUS_EXCESS_ACCEL,
  VehicleStatusCode.STATUS_EXCESS_ACCEL_2,
  VehicleStatusCode.STATUS_EXCESS_ACCEL_3,
  VehicleStatusCode.STATUS_SPEEDING_BEGIN,
  VehicleStatusCode.STATUS_SEATBELT_OFF_0,
  VehicleStatusCode.STATUS_SEATBELT_OFF_1,
  VehicleStatusCode.STATUS_SEATBELT_OFF_2,
  VehicleStatusCode.STATUS_SEATBELT_OFF_3,
]);

export const isIdleEvent = (point: VehicleMapDataPoint) =>
  IDLE_CODES.has(point.statuscode);
export const isStoppedEvent = (point: VehicleMapDataPoint) =>
  STOPPED_CODES.has(point.statuscode);
export const isMovingEvent = (point: VehicleMapDataPoint) =>
  MOTION_STATUS_CODES.has(point.statuscode);

// How long something lasted. Deliberately not humanReadableTimeDuration,
// which answers "how long ago" - it renders zero as "now" and keeps only
// the largest unit, so a trip that never idled reported "Idle Time: now"
// and a 2m30s stop read as "2m".
export const formatDuration = (seconds: number): string => {
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = whole % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0)
    return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
  return `${remainder}s`;
};

export type TripSummary = {
  // how many separate times it sat idling, not how many idle events came in
  idleCount: number;
  idleSeconds: number;
  // likewise: separate stops, not the number of "Stopped" events reported
  stoppedCount: number;
  stoppedSeconds: number;
  maxSpeedKph: number;
  violationCount: number;
};

export type TripSummaryResult = {
  summary: TripSummary;
  // For a point that is part of a stopped or idling run, the length of that
  // whole run - not this one point's share of it. A parked asset reports
  // from the same spot thirty times and the useful answer on every one of
  // those rows is "stopped here for 12 minutes", not thirty slivers.
  //
  // Keyed by point identity: the list renders the same objects it was given.
  runSecondsByPoint: Map<VehicleMapDataPoint, number>;
};

const EMPTY: TripSummaryResult = {
  summary: {
    idleCount: 0,
    idleSeconds: 0,
    stoppedCount: 0,
    stoppedSeconds: 0,
    maxSpeedKph: 0,
    violationCount: 0,
  },
  runSecondsByPoint: new Map(),
};

// The same figures for a whole day rather than one trip, for the roll-up
// under the date row. Counts and durations add up across the day's trips;
// max speed is the highest of them, not a sum.
export type DaySummary = Omit<TripSummary, "violationCount">;

export const summariseDay = (
  trips: VehicleMapDataPoint[][]
): DaySummary =>
  trips.reduce<DaySummary>(
    (total, tripPoints) => {
      const { summary } = summariseTrip(tripPoints);
      return {
        idleCount: total.idleCount + summary.idleCount,
        idleSeconds: total.idleSeconds + summary.idleSeconds,
        stoppedCount: total.stoppedCount + summary.stoppedCount,
        stoppedSeconds: total.stoppedSeconds + summary.stoppedSeconds,
        maxSpeedKph: Math.max(total.maxSpeedKph, summary.maxSpeedKph),
      };
    },
    {
      idleCount: 0,
      idleSeconds: 0,
      stoppedCount: 0,
      stoppedSeconds: 0,
      maxSpeedKph: 0,
    }
  );

export const summariseTrip = (
  tripPoints: VehicleMapDataPoint[]
): TripSummaryResult => {
  if (tripPoints.length === 0) return EMPTY;

  // the points arrive in report order, but a trip stitched from more than
  // one source can land out of order, and every duration below is a
  // difference between neighbours
  const points = [...tripPoints].sort((a, b) => a.timestamp - b.timestamp);

  const runSecondsByPoint = new Map<VehicleMapDataPoint, number>();
  let idleCount = 0;
  let idleSeconds = 0;
  let stoppedCount = 0;
  let stoppedSeconds = 0;
  let maxSpeedKph = 0;
  let violationCount = 0;

  // a run is a stretch of consecutive points of the same kind; its length
  // runs to the first point that isn't, which is when the asset moved again
  let runStartIndex: number | null = null;
  let runIsIdle = false;


  const closeRun = (endIndex: number, endIsMember = false) => {
    if (runStartIndex === null) return;
    const seconds = Math.max(
      0,
      points[endIndex].timestamp - points[runStartIndex].timestamp
    );
    const lastMemberIndex = endIsMember ? endIndex : endIndex - 1;
    for (let index = runStartIndex; index <= lastMemberIndex; index++)
      runSecondsByPoint.set(points[index], seconds);
    if (runIsIdle) {
      idleCount += 1;
      idleSeconds += seconds;
    } else {
      stoppedCount += 1;
      stoppedSeconds += seconds;
    }
    runStartIndex = null;
  };

  points.forEach((point, index) => {
    if (point.speedkph > maxSpeedKph) maxSpeedKph = point.speedkph;
    // one per event, flat. Duplicate suppression - the same infraction
    // arriving from both the tracker and the camera - is already done
    // upstream, so everything that reaches here is a real violation and
    // collapsing any of it would undercount.
    if (VIOLATION_CODES.has(point.statuscode)) violationCount += 1;

    const idle = isIdleEvent(point);
    const stopped = isStoppedEvent(point);
    if (!idle && !stopped) {
      closeRun(index);
      return;
    }
    // idling and standing still are different runs even back to back
    if (runStartIndex !== null && runIsIdle !== idle) closeRun(index);
    if (runStartIndex === null) {
      runStartIndex = index;
      runIsIdle = idle;
    }
  });
  // A trip that ends mid-run has no following point to measure against, so
  // the run closes on its own last report. That report is part of the run,
  // so it is labelled too - without endIsMember the final row of a trip
  // that ends parked showed no duration at all.
  closeRun(points.length - 1, true);

  return {
    summary: {
      idleCount,
      idleSeconds,
      stoppedCount,
      stoppedSeconds,
      maxSpeedKph,
      violationCount,
    },
    runSecondsByPoint,
  };
};
